"use client";

import { useState } from "react";
import dynamic from "next/dynamic";
import TodaySection from "@/components/sections/TodaySection";
import { NavigationTabs } from "@/components/ui/navigation-tabs";
import { SectionTransition, PersistentLayer } from "@/components/ui/SectionTransition";

// Defer non-initial sections out of the first JS bundle. They each pull in
// GSAP + ScrollTrigger; admin modals also pull in the Supabase client. None
// of this needs to parse on the main thread before the user navigates away
// from the Today section.
const YesterdaySection = dynamic(() =>
  import("@/components/sections/YesterdaySection").then((m) => m.YesterdaySection)
);
const TomorrowSection = dynamic(() => import("@/components/sections/TomorrowSection"));
const AboutSection = dynamic(() => import("@/components/sections/AboutSection"));
const AdminModal = dynamic(() => import("@/components/admin/AdminModal"), {
  ssr: false,
});
const AdminLoginModal = dynamic(() => import("@/components/admin/AdminLoginModal"), {
  ssr: false,
});

type MediaItem = { type: "video" | "image"; src: string; alt?: string };

type Project = {
  id: string;
  company: string;
  city: string;
  date: string;
  thumb: string;
  services: string[];
  sort_order: number;
};

type Service = {
  id?: number;
  tag: string;
  title: string;
  items: string[];
  sort_order?: number;
};

type TodayConfig = {
  items: MediaItem[];
  time_per_slide: number;
};

export default function HomeShell({
  projects,
  services,
  todayConfig,
  isAdmin: initialIsAdmin,
}: {
  projects: Project[];
  services: Service[];
  todayConfig: TodayConfig;
  isAdmin: boolean;
}) {
  const [currentSection, setCurrentSection] = useState<string>("today");
  const [adminOpen, setAdminOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(initialIsAdmin);
  const [contentReady, setContentReady] = useState(false);

  function handleLoginSuccess() {
    setLoginOpen(false);
    setIsAdmin(true);
  }

  const mediaConfig = {
    items: todayConfig.items,
    timePerSlide: todayConfig.time_per_slide,
  };

  return (
    <div
      className="relative flex size-full"
      style={{
        background:
          "linear-gradient(180deg, #d7dae0 0%, #bfc3cb 45%, #a7abb5 100%)",
      }}
    >
      {/* Today is always mounted to keep its WebGL canvas warm and avoid re-init lag on nav. */}
      <PersistentLayer active={currentSection === "today"}>
        <TodaySection
          mediaConfig={mediaConfig}
          onAboutClick={() => setCurrentSection("about")}
          onReady={() => setContentReady(true)}
        />
      </PersistentLayer>

      {currentSection !== "today" && (
        <SectionTransition sectionKey={currentSection}>
          {currentSection === "yesterday" && <YesterdaySection projects={projects} />}
          {currentSection === "about" && <AboutSection />}
          {currentSection === "tomorrow" && <TomorrowSection services={services} />}
        </SectionTransition>
      )}

      <NavigationTabs
        currentSection={currentSection}
        setCurrentSection={setCurrentSection}
        ready={contentReady}
      />

      {/* Tiny shield — login trigger on Today section when not yet admin */}
      {!isAdmin && currentSection === "today" && (
        <button
          type="button"
          onClick={() => setLoginOpen(true)}
          title="Admin login"
          aria-label="Admin login"
          className="absolute opacity-0 lg:opacity-100 top-1 right-1 z-50 p-1.5 text-white/70 hover:text-white transition-colors"
        >
          <svg width="8" height="8" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z" />
          </svg>
        </button>
      )}

      {/* Full admin button — visible when logged in */}
      {isAdmin && (
        <button
          type="button"
          onClick={() => setAdminOpen(true)}
          title="Admin panel"
          className="absolute top-4 right-4 z-50 flex items-center gap-1.5 rounded-sm bg-[#2C3030] text-white/80 px-3 py-2 text-xs font-medium tracking-wide hover:bg-[#0009FF] hover:text-black transition-colors"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z" />
          </svg>
          [ admin ]
        </button>
      )}

      {adminOpen && (
        <AdminModal
          onClose={() => setAdminOpen(false)}
          onLogout={() => setIsAdmin(false)}
        />
      )}

      {loginOpen && (
        <AdminLoginModal
          onSuccess={handleLoginSuccess}
          onClose={() => setLoginOpen(false)}
        />
      )}
    </div>
  );
}
